class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // this.dragScrollElements = new WeakSet();
    this.initBundleProductsDragScroll();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
    this.observeCartEmptyState();
  }

  initBundleProductsDragScroll() {
    // Guard so this only ever binds once per <cart-drawer> instance,
    // regardless of how many times open()/renderContents() call it.
    if (this._dragScrollBound) return;
    this._dragScrollBound = true;

    let activeSlider = null;
    let isDown = false;
    let isDragging = false;
    let preventClick = false;
    let startX = 0;
    let scrollLeft = 0;
    let pointerId = null;

    // Elements that should never start a drag (subscription select, qty inputs, buttons, links)
    const isInteractive = (el) => !!el.closest('select, button, input, a, textarea');

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (event.button !== 0) return;

      const slider = event.target.closest('.bundle--products-list');
      if (!slider || !this.contains(slider)) return;
      if (isInteractive(event.target)) return; // don't hijack the frequency <select>, buttons, etc.

      activeSlider = slider;
      isDown = true;
      isDragging = false;
      preventClick = false;
      pointerId = event.pointerId;

      startX = event.clientX;
      scrollLeft = slider.scrollLeft;

      slider.classList.add('is-dragging');
      slider.setPointerCapture(pointerId);
    });

    this.addEventListener('pointermove', (event) => {
      if (!isDown || !activeSlider || event.pointerId !== pointerId) return;

      const distance = event.clientX - startX;

      if (Math.abs(distance) > 5) {
        isDragging = true;
        preventClick = true;
      }

      if (isDragging) {
        event.preventDefault();
      }

      activeSlider.scrollLeft = scrollLeft - distance;
    });

    this.addEventListener('pointerup', (event) => {
      if (!activeSlider) return;

      if (pointerId !== null && activeSlider.hasPointerCapture(pointerId)) {
        activeSlider.releasePointerCapture(pointerId);
      }

      activeSlider.classList.remove('is-dragging');
      isDown = false;
      pointerId = null;
      activeSlider = null;
    });

    this.addEventListener('pointercancel', () => {
      if (!activeSlider) return;
      activeSlider.classList.remove('is-dragging');
      isDown = false;
      pointerId = null;
      activeSlider = null;
    });

    this.addEventListener(
      'click',
      (event) => {
        if (!preventClick) return;
        const slider = event.target.closest('.bundle--products-list');
        if (!slider) return;

        event.preventDefault();
        event.stopPropagation();
        preventClick = false;
      },
      true
    );
  }

  observeCartEmptyState() {
    if (this._emptyObserver) return; // only ever bind once per instance

    const target = this.querySelector('#CartDrawer') || this;

    const checkEmpty = () => {
      const hasItems = !!this.querySelector('.cart-item');
      this.classList.toggle('is-empty', !hasItems);
    };

    checkEmpty(); // run once immediately on init

    this._emptyObserver = new MutationObserver(checkEmpty);
    this._emptyObserver.observe(target, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.initBundleProductsDragScroll();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Here the animation doesn't seem to always get triggered. A timeout seems to help.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  
  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    
    if (parsedState.id !== undefined) {
      this.productId = parsedState.id;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHTML) {
        // console.error('[cart-drawer] missing section in response:', section.id);
        return; // NEW: skip this section instead of crashing the whole forEach
      }

      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (innerHTML === null) {
        // console.error('[cart-drawer] selector not found in section HTML:', section.selector);
        return; // NEW
      }

      sectionElement.innerHTML = innerHTML;
    });

    this.initBundleProductsDragScroll();

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.open();
    });
  }

  // getSectionInnerHTML(html, selector = '.shopify-section') {
  //   return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  // }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const target = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return target ? target.innerHTML : null; // never throw on a mismatch, let the caller decide
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);